import { Inject, Injectable } from '@angular/core';
import { CalendarOptions, EventApi } from '@fullcalendar/core';
import interactionPlugin from '@fullcalendar/interaction';
import resourceDayGridPlugin from '@fullcalendar/resource-daygrid';
import resourceTimeGridPlugin from '@fullcalendar/resource-timegrid';
import scrollGridPlugin from '@fullcalendar/scrollgrid';
import { IconName } from '@sc/data-access-assets';
import {
  ConfigurationService,
  TimezoneService,
} from '@sc/data-access-configuration';
import {
  AgendaEvent,
  CLIENT_APP_ID,
  ClientAppId,
  Dossier,
  DossierGlobalOrigins,
  findCorrespondingServiceType,
  Fitter,
  FitterOpeningPeriod,
  PartAvailabilityStatus,
  recurentAgendaEventTransform,
  RecurrentAgendaEvent,
  RecurrentInstanceAgendaEvent,
} from '@sc/data-access-models';
import { JsonListKey, StorageService } from '@sc/data-access-storage';
import { TranslatorService } from '@sc/data-access-translation';
import { AuthService } from '@sc/feature-auth-okta';
import {
  CalendarMode,
  DossierEventExtendedProps,
  GenericEventExtendedProps,
  Resource,
  ResourceBusinessHour,
  ResourcesManagerService,
  ResourceType,
} from '@sc/feature-fitter-shared-libs';
import { Task } from '@sc/ui-components';
import { dayjs, getPeriodReplacementVehicleUse } from '@sc/utils';
import { Dayjs } from 'dayjs';
import { interval, noop, Observable } from 'rxjs';
import { filter, map, take, tap } from 'rxjs/operators';
import { ICONS } from '../const';

@Injectable({ providedIn: 'root' })
export class PlanningCalendarService {
  defaultConf: (mode: CalendarMode) => Partial<CalendarOptions> = (
    mode: CalendarMode
  ) => ({
    schedulerLicenseKey: '0942796834-fcs-1693527644',
    locale: 'fr',
    timeZone: 'UTC',
    editable: true, // allow drag & drop
    dayMinWidth: 150, // min width day
    slotEventOverlap: false, // don't overlap event on calendar view
    slotDuration: { minute: this.getDuration() }, // 10 min slot duration by default
    scrollTimeReset: false,
    slotLabelInterval: { hour: 1 }, // y-axis label - interval
    nowIndicator: true, // display current time on calendar
    nowIndicatorClassNames: ['now-indicator-revised'],
    datesAboveResources: true,
    initialView: mode,
    headerToolbar: false,
    allDaySlot: false,
    firstDay: 1,
    resourceOrder: 'order,id,title',
    views: {
      resourceTimeGridWeek: {
        type: 'resourceTimeGrid',
        dateAlignement: 'week',
      },
    },
    events: [],
    plugins: [
      interactionPlugin,
      scrollGridPlugin,
      resourceTimeGridPlugin,
      resourceDayGridPlugin,
    ],
  });

  constructor(
    private readonly timezoneService: TimezoneService,
    private readonly authOktaService: AuthService,
    private readonly translator: TranslatorService,
    private readonly resManagerService: ResourcesManagerService,
    private readonly storage: StorageService,
    @Inject(CLIENT_APP_ID) private readonly clientAppId: ClientAppId
  ) {}

  getDuration() {
    return this.storage.getItem('slotDuration', localStorage, 10);
  }

  getStorageLanguage() {
    return this.storage.getItem('dossier-language');
  }

  choseOpeningPeriod(
    fitter: Fitter,
    type: ResourceType
  ): FitterOpeningPeriod[] {
    switch (type) {
      case 'OUTSIDE':
      case 'INTERVENTION_VEHICLE':
        // Use outside if available, use center as fallback
        if (fitter.openingHours?.outside?.length) {
          return fitter.openingHours?.outside;
        }
        return fitter.openingHours?.center ?? [];
      case 'IN_CENTER':
      case 'REPLACEMENTVEHICLE':
      case 'TECHNICIAN':
      case 'RV_DETAILED':
      default:
        // Use center if available
        return fitter.openingHours?.center ?? [];
    }
  }

  construcBusinessHoursFromOpeningPeriod(
    periods: FitterOpeningPeriod[]
  ): ResourceBusinessHour[] {
    if (periods) {
      const formatHour = (hour: string) =>
        `${hour.slice(0, 2)}:${hour.slice(2, 4)}`;

      const businessHours = periods.map((c) => {
        const { day: openDay, time: openTime } = c.open;
        const { time: closeTime } = c.close;

        return {
          daysOfWeek: [openDay],
          startTime: formatHour(openTime),
          endTime: formatHour(closeTime),
        };
      });

      return businessHours;
    }

    return [];
  }

  constructCalendarResource(
    fitter: Fitter,
    type: ResourceType,
    resName?: string,
    display = true
  ): Resource {
    const periods = this.choseOpeningPeriod(fitter, type);

    const businessHours = this.construcBusinessHoursFromOpeningPeriod(periods);

    const fitterComment = this.getOwnFitterComment(fitter);

    return {
      id: this.resManagerService.constructResourceId(fitter.id, type, resName),
      fitterId: fitter.id,
      brand: fitter.brand,
      city: fitter.city,
      type: type,
      display: display,
      timezone: this.timezoneService.getFitterTimezone(fitter),
      businessHours,
      fitterComment,
    };
  }

  /**
    The only way to detect dynamicaly if the technician view is detailed is if the default technician column exists
  */
  filtersAreTechnicianView(filters: Task[]) {
    return (
      filters?.findIndex(
        (filter) =>
          filter.subtasks?.findIndex((subFilter) =>
            this.resManagerService.isDefaultTechnician(subFilter.id)
          ) > -1
      ) > -1
    );
  }

  /**
    The only way to detect dynamicaly if the interventionVehicle view is detailed is if the default interventionVehicle column exists
  */
  filtersAreInterventionVehicleView(filters: Task[]) {
    return (
      filters?.findIndex(
        (filter) =>
          filter.subtasks?.findIndex((subFilter) =>
            this.resManagerService.isDefaultInterventionVehicle(subFilter.id)
          ) > -1
      ) > -1
    );
  }

  /**
    The only way to detect dynamicaly if the RV view is detailed is if the default RV column exists
  */
  filtersAreRVView(filters: Task[]) {
    return (
      filters?.findIndex(
        (filter) =>
          filter.subtasks?.findIndex((subFilter) =>
            this.resManagerService.isDefaultRV(subFilter.id)
          ) > -1
      ) > -1
    );
  }

  shouldDisplayDossierEvent(
    filters: Task[],
    dossier: Dossier,
    fitter: Fitter
  ): boolean {
    // Some dossier events are only displayed if the right technician filters are checked (only if columns view is not technician)
    let shouldDisplay = true;
    if (filters?.length && !this.filtersAreTechnicianView(filters)) {
      const technicianFilter = filters
        ?.find((filter) => filter?.id == fitter?.id)
        ?.subtasks?.find(
          (subTask) =>
            this.resManagerService.getResourceType(subTask?.id) === 'IN_CENTER'
        )
        ?.subtasks?.find((technician) => technician?.checked);

      shouldDisplay = this.shouldDisplayEventByTechnicianFilter(
        dossier.appointment.assignedResources,
        technicianFilter?.id
      );
    }

    // Some dossier events are only displayed if the right interventionVehicle filters is checked (only if columns view is not interventionVehicle)
    if (
      shouldDisplay &&
      filters?.length &&
      !this.filtersAreInterventionVehicleView(filters) &&
      !!dossier.appointment?.outsideIntervention
    ) {
      const interventionVehicleFilter = filters
        ?.find((filter) => filter?.id == fitter?.id)
        ?.subtasks?.find(
          (subTask) =>
            this.resManagerService.getResourceType(subTask?.id) === 'OUTSIDE'
        )
        ?.subtasks?.find((interventionVehicle) => interventionVehicle?.checked);

      shouldDisplay = this.shouldDisplayEventByInterventionVehicleFilter(
        dossier.appointment.assignedInterventionVehicle?.length
          ? [dossier.appointment.assignedInterventionVehicle]
          : null,
        interventionVehicleFilter?.id
      );
    }
    return shouldDisplay;
  }

  shouldDisplayAgendaEvent(
    filters: Task[],
    event: AgendaEvent,
    fitter: Fitter,
    location: 'center' | 'outside'
  ): boolean {
    // Some generic events are only displayed if the right technician filters are checked (only if columns view is not technician)
    let shouldDisplay = true;
    if (
      filters?.length &&
      !this.filtersAreTechnicianView(filters) &&
      (event?.type === 'TECH_LEAVE' ||
        (event?.type === 'GENERIC' && location === 'center'))
    ) {
      const technicianFilter = filters
        ?.find((filter) => filter?.id == fitter?.id)
        ?.subtasks?.find(
          (subTask) =>
            this.resManagerService.getResourceType(subTask?.id) === 'IN_CENTER'
        )
        ?.subtasks?.find((technician) => technician?.checked);

      shouldDisplay = this.shouldDisplayEventByTechnicianFilter(
        event.resources?.map((res) => res.name) || [],
        technicianFilter?.id
      );
    }

    // Some generic events are only displayed if the right interventionVehicle filters are checked (only if columns view is not interventionVehicle)
    if (
      filters?.length &&
      !this.filtersAreInterventionVehicleView(filters) &&
      (event?.type === 'INTERVENTION_VEHICLE_LEAVE' ||
        (event?.type === 'GENERIC' && location === 'outside'))
    ) {
      const interventionVehicleFilter = filters
        ?.find((filter) => filter?.id == fitter?.id)
        ?.subtasks?.find(
          (subTask) =>
            this.resManagerService.getResourceType(subTask?.id) === 'OUTSIDE'
        )
        ?.subtasks?.find((interventionVehicle) => interventionVehicle?.checked);

      shouldDisplay = this.shouldDisplayEventByInterventionVehicleFilter(
        event.resources?.map((res) => res.name) || [],
        interventionVehicleFilter?.id
      );
    }
    return shouldDisplay;
  }

  shouldDisplayEventByTechnicianFilter(
    assignedTechnicians: string[],
    technicianFilterId: string
  ): boolean {
    let shouldDisplay = false;

    if (technicianFilterId?.includes('allTechnicians')) {
      shouldDisplay = true;
    } else if (technicianFilterId?.includes('notSetTechnician')) {
      shouldDisplay = !assignedTechnicians?.length;
    } else if (assignedTechnicians?.length) {
      shouldDisplay = assignedTechnicians?.includes(
        technicianFilterId?.split('_')[1]
      );
    }
    return shouldDisplay;
  }

  shouldDisplayEventByInterventionVehicleFilter(
    assignedInterventionVehicle: string[],
    interventionVehicleFilterId: string
  ): boolean {
    let shouldDisplay = false;

    if (interventionVehicleFilterId?.includes('allInterventionVehicles')) {
      shouldDisplay = true;
    } else if (
      interventionVehicleFilterId?.includes('noSetInterventionVehicle')
    ) {
      shouldDisplay = !assignedInterventionVehicle?.length;
    } else if (assignedInterventionVehicle) {
      shouldDisplay = assignedInterventionVehicle?.includes(
        interventionVehicleFilterId?.split('_')[1]
      );
    }
    return shouldDisplay;
  }

  buildDossierEvent(
    dossier: Dossier,
    fitter: Fitter,
    resourceId: string,
    display: boolean,
    editable: boolean,
    startTime: string,
    endTime?: string
  ): EventApi {
    const extendedProps: DossierEventExtendedProps = {
      dossier,
      fitter,
      display: display,
    };

    return {
      resourceId: resourceId,
      classNames: ['dossier'],
      start: startTime,
      end: endTime ?? startTime,
      borderColor: '#00000000',
      color: '#00000000',
      extendedProps: extendedProps,
      editable: editable,
      resourceEditable: editable,
    } as unknown as EventApi;
  }

  buildGenericEvent(
    event: AgendaEvent,
    fitter: Fitter,
    resourceId: string,
    display: boolean,
    startTime: string,
    endTime?: string
  ): EventApi {
    const extendedProps: GenericEventExtendedProps = {
      event,
      fitter,
      display: display,
    };

    return {
      resourceId: resourceId,
      classNames: ['event'],
      start: startTime,
      end: endTime ?? startTime,
      color: 'var(--color-grey-extra-light)',
      borderColor: 'var(--color-grey-main)',
      extendedProps: extendedProps,
      editable: false,
      resourceEditable: false,
    } as unknown as EventApi;
  }

  constructAllCalendarDossierEvents(
    dossier: Dossier,
    fitters: Fitter[]
  ): EventApi[] {
    const displayCancelled = this.storage.getItem(
      'calendar-display-cancelled',
      localStorage,
      true
    );

    // Dossiers to hide on calendar
    if (
      !dossier.appointment.isValidatedByFitter ||
      dossier.state === 'ABANDONED' ||
      !dossier ||
      this.isDossierDraft(dossier) ||
      (!displayCancelled && dossier.appointment?.status === 'CANCELLED')
    ) {
      return [];
    }

    const resultEvents: EventApi[] = [];

    const fitter = fitters.find(
      (fitter) => fitter.id === dossier.appointment.fitterId
    );

    const filters = this.storage.getItem<JsonListKey, Task>(
      'agenda-resource-filters'
    );

    if (
      !dayjs(dossier.appointment.time).isValid() ||
      !dayjs(dossier.appointment.endTime).isValid()
    ) {
      console.warn('Invalid Time for dossier :', dossier?.id);
      return [];
    }

    const startTime = this.computeTimeOffsetForCalendar(
      dossier.appointment.time,
      this.timezoneService.getFitterTimezone(fitter)
    );
    const endTime = this.computeTimeOffsetForCalendar(
      dossier.appointment.endTime,
      this.timezoneService.getFitterTimezone(fitter)
    );
    const rvUsePeriod = getPeriodReplacementVehicleUse(dossier, {
      start: dossier.appointment.time,
      end: dossier.appointment.endTime,
    });
    const rvUsePeriodCalTimezone: typeof rvUsePeriod = {
      start: this.computeTimeOffsetForCalendar(
        rvUsePeriod.start,
        this.timezoneService.getFitterTimezone(fitter)
      ),
      end: this.computeTimeOffsetForCalendar(
        rvUsePeriod.end,
        this.timezoneService.getFitterTimezone(fitter)
      ),
    };

    const shouldDisplay = this.shouldDisplayDossierEvent(
      filters,
      dossier,
      fitter
    );

    if (dossier.appointment.outsideIntervention) {
      if (this.filtersAreInterventionVehicleView(filters)) {
        // If the interventionVehicles are all deleted from the fitter, we place the event in the N/A column
        const resourceId =
          dossier.appointment?.assignedInterventionVehicle &&
          fitter.interventionVehicles.find(
            (interventionVehicle) =>
              interventionVehicle.name ===
              dossier.appointment?.assignedInterventionVehicle
          )?.active
            ? this.resManagerService.constructResourceId(
                dossier.appointment.fitterId,
                'INTERVENTION_VEHICLE',
                dossier.appointment.assignedInterventionVehicle
              )
            : this.resManagerService.constructDefaultInterventionVehicleId(
                dossier.appointment.fitterId
              );
        resultEvents.push(
          this.buildDossierEvent(
            dossier,
            fitter,
            resourceId,
            true,
            true,
            startTime,
            endTime
          )
        );
      } else {
        // Adding dossier event for outside intervention column
        const resourceId = this.resManagerService.constructResourceId(
          dossier.appointment.fitterId,
          'OUTSIDE'
        );

        resultEvents.push(
          this.buildDossierEvent(
            dossier,
            fitter,
            resourceId,
            shouldDisplay,
            true,
            startTime,
            endTime
          )
        );
      }
    } else {
      if (this.filtersAreTechnicianView(filters)) {
        // If the technicians are all deleted from the fitter, we place the event in the N/A column
        const existingTechs = dossier.appointment.assignedResources?.filter(
          (apmtTech) =>
            fitter.resources?.findIndex(
              (fitterTech) => fitterTech.name === apmtTech
            ) > -1
        );
        if (
          existingTechs?.length > 0 &&
          existingTechs.some(
            (tech) =>
              fitter.resources.find((resource) => resource.name === tech)
                ?.active
          )
        ) {
          // Adding dossier event for each technician column corresponding to the technicians assigned to the dossier
          dossier.appointment.assignedResources.forEach((dossierTech) => {
            const resourceId = this.resManagerService.constructResourceId(
              dossier.appointment.fitterId,
              'TECHNICIAN',
              dossierTech
            );

            resultEvents.push(
              this.buildDossierEvent(
                dossier,
                fitter,
                resourceId,
                true,
                true,
                startTime,
                endTime
              )
            );
          });
        } else {
          // Adding dossier event for in the default technician column if no technician is assigned to the dossier
          const resourceId =
            this.resManagerService.constructDefaultTechResourceId(
              dossier.appointment.fitterId
            );

          resultEvents.push(
            this.buildDossierEvent(
              dossier,
              fitter,
              resourceId,
              true,
              true,
              startTime,
              endTime
            )
          );
        }
      } else {
        // Adding dossier event for in center intervention column (only if columns view is not technician)
        const resourceId = this.resManagerService.constructResourceId(
          dossier.appointment.fitterId,
          'IN_CENTER'
        );

        resultEvents.push(
          this.buildDossierEvent(
            dossier,
            fitter,
            resourceId,
            shouldDisplay,
            true,
            startTime,
            endTime
          )
        );
      }
    }

    if (dossier.appointment.isReplacementVehicleSelected) {
      if (this.filtersAreRVView(filters)) {
        let resourceId: string;
        if (dossier.appointment?.assignedReplacementVehicle) {
          // Adding dossier event in the RV column corresponding to RV assigned to the dossier
          resourceId = this.resManagerService.constructResourceId(
            dossier.appointment.fitterId,
            'RV_DETAILED',
            dossier.appointment?.assignedReplacementVehicle?.id +
              '##' +
              dossier.appointment?.assignedReplacementVehicle?.name
          );
        } else {
          // Adding dossier event for in the default RV column if no RV is assigned to the dossier
          resourceId = this.resManagerService.constructDefaultRVResourceId(
            dossier.appointment.fitterId
          );
        }

        resultEvents.push(
          this.buildDossierEvent(
            dossier,
            fitter,
            resourceId,
            shouldDisplay,
            true,
            rvUsePeriodCalTimezone.start,
            rvUsePeriodCalTimezone.end
          )
        );
      } else {
        // Adding dossier event for replacement vehicle column
        const resourceId = this.resManagerService.constructResourceId(
          dossier.appointment.fitterId,
          'REPLACEMENTVEHICLE'
        );

        resultEvents.push(
          this.buildDossierEvent(
            dossier,
            fitter,
            resourceId,
            shouldDisplay,
            true,
            rvUsePeriodCalTimezone.start,
            rvUsePeriodCalTimezone.end
          )
        );
      }
    } else if (
      dossier?.appointment?.isReplacementVehicleSelected &&
      !dossier?.appointment?.assignedReplacementVehicle &&
      this.filtersAreRVView(filters)
    ) {
      // Adding dossier event for replacement vehicle default column
      const resourceId = this.resManagerService.constructDefaultRVResourceId(
        dossier.appointment.fitterId
      );
      resultEvents.push(
        this.buildDossierEvent(
          dossier,
          fitter,
          resourceId,
          shouldDisplay,
          true,
          rvUsePeriodCalTimezone.start,
          rvUsePeriodCalTimezone.end
        )
      );
    }

    return resultEvents;
  }

  constructAllCalendarGenericEvent(
    event: AgendaEvent,
    fitters: Fitter[],
    timeFrame?: { start: Dayjs; end: Dayjs }
  ) {
    const resultEvents: EventApi[] = [];

    // Some events, such as HOLYDAYS can be inactive and therefore shouldn't appear on calendar
    if (event.type === 'HOLYDAY' && !event.additionalData?.active) {
      return [];
    }

    if (this.isEventRecurrent(event)) {
      const generatedEvents = recurentAgendaEventTransform(
        event,
        timeFrame?.start || dayjs(),
        timeFrame?.end || dayjs().add(1, 'month')
      );

      generatedEvents.forEach((generatedEvent) =>
        resultEvents.push(
          ...this.constructAllCalendarGenericEvent(
            generatedEvent,
            fitters,
            timeFrame
          )
        )
      );

      return resultEvents;
    }

    const fitter = fitters.find((fitter) => fitter.id === event.fitterId);

    if (!dayjs(event.startTime).isValid() || !dayjs(event.endTime).isValid()) {
      console.warn('Invalid Time for event :', event?.id);
      return [];
    }

    const startTime = this.computeTimeOffsetForCalendar(
      event.startTime,
      this.timezoneService.getFitterTimezone(fitter)
    );
    const endTime = this.computeTimeOffsetForCalendar(
      event.endTime,
      this.timezoneService.getFitterTimezone(fitter)
    );

    const filters = this.storage.getItem<JsonListKey, Task>(
      'agenda-resource-filters',
      localStorage,
      []
    );

    switch (event.type) {
      case 'RV_LEAVE':
        this.addReplacementLeaveEvent(
          resultEvents,
          filters,
          event,
          fitter,
          startTime,
          endTime
        );
        break;
      case 'TECH_LEAVE':
        this.addTechLeaveEventOrGenericInCenterEvent(
          resultEvents,
          filters,
          event,
          fitter,
          this.shouldDisplayAgendaEvent(filters, event, fitter, 'center'),
          startTime,
          endTime
        );
        break;
      case 'INTERVENTION_VEHICLE_LEAVE':
        this.addInterventionVehicleLeaveEventOrGenericOutsideEvent(
          resultEvents,
          filters,
          event,
          fitter,
          this.shouldDisplayAgendaEvent(filters, event, fitter, 'outside'),
          startTime,
          endTime
        );
        break;
      default:
        if (!event.locations?.length || event.locations.includes('center')) {
          this.addTechLeaveEventOrGenericInCenterEvent(
            resultEvents,
            filters,
            event,
            fitter,
            this.shouldDisplayAgendaEvent(filters, event, fitter, 'center'),
            startTime,
            endTime
          );
        }
        if (event.locations?.includes('outside')) {
          this.addInterventionVehicleLeaveEventOrGenericOutsideEvent(
            resultEvents,
            filters,
            event,
            fitter,
            this.shouldDisplayAgendaEvent(filters, event, fitter, 'outside'),
            startTime,
            endTime
          );
        }

        break;
    }

    return resultEvents;
  }

  addReplacementLeaveEvent(
    resultEvents: EventApi[],
    filters: Task[],
    event: AgendaEvent,
    fitter: Fitter,
    startTime: string,
    endTime: string
  ) {
    if (this.filtersAreRVView(filters)) {
      if (event.resources?.length > 0) {
        // Adding generic event for each RV column corresponding to the RV assigned to the event
        // FIXME -  don't use resources to stock RV inside agenda events
        event.resources.forEach((eventRes) => {
          const rv = fitter.vehicles.find(
            (vehicle) => vehicle.name === eventRes.name
          );
          const resourceId = this.resManagerService.constructResourceId(
            event.fitterId,
            'RV_DETAILED',
            rv?.id + '##' + eventRes.name
          );

          resultEvents.push(
            this.buildGenericEvent(
              event,
              fitter,
              resourceId,
              true,
              startTime,
              endTime
            )
          );
        });
      } else {
        // Adding generic event in all the RV columns if no RV is assigned to the event
        fitter.vehicles?.forEach((fitterRV) => {
          const resourceId = this.resManagerService.constructResourceId(
            event.fitterId,
            'RV_DETAILED',
            fitterRV.id + '##' + fitterRV.name
          );

          resultEvents.push(
            this.buildGenericEvent(
              event,
              fitter,
              resourceId,
              true,
              startTime,
              endTime
            )
          );
        });
      }
    } else {
      // Adding generic event for RV intervention column (only if columns view is not detailled for RV)
      const resourceId = this.resManagerService.constructResourceId(
        event.fitterId,
        'REPLACEMENTVEHICLE'
      );

      resultEvents.push(
        this.buildGenericEvent(
          event,
          fitter,
          resourceId,
          true,
          startTime,
          endTime
        )
      );
    }
  }

  addTechLeaveEventOrGenericInCenterEvent(
    resultEvents: EventApi[],
    filters: Task[],
    event: AgendaEvent,
    fitter: Fitter,
    shouldDisplay: boolean,
    startTime: string,
    endTime: string
  ) {
    if (this.filtersAreTechnicianView(filters)) {
      if (event.resources?.length) {
        // Adding generic event for each technician column corresponding to the technicians assigned to the event
        event.resources.forEach((eventTech) => {
          const resourceId = this.resManagerService.constructResourceId(
            event.fitterId,
            'TECHNICIAN',
            eventTech.name
          );

          resultEvents.push(
            this.buildGenericEvent(
              event,
              fitter,
              resourceId,
              true,
              startTime,
              endTime
            )
          );
        });
      } else {
        // Adding generic event in all the technician columns if no technician is assigned to the event
        fitter.resources?.forEach((fitterTech) => {
          const resourceId = this.resManagerService.constructResourceId(
            event.fitterId,
            'TECHNICIAN',
            fitterTech.name
          );

          resultEvents.push(
            this.buildGenericEvent(
              event,
              fitter,
              resourceId,
              true,
              startTime,
              endTime
            )
          );
        });

        resultEvents.push(
          this.buildGenericEvent(
            event,
            fitter,
            this.resManagerService.constructDefaultTechResourceId(
              event.fitterId
            ),
            true,
            startTime,
            endTime
          )
        );
      }
    } else {
      // Adding Tech leave event for in center intervention column (only if columns view is not technician)
      const resourceId = this.resManagerService.constructResourceId(
        event.fitterId,
        'IN_CENTER'
      );

      resultEvents.push(
        this.buildGenericEvent(
          event,
          fitter,
          resourceId,
          shouldDisplay,
          startTime,
          endTime
        )
      );
    }
  }

  addInterventionVehicleLeaveEventOrGenericOutsideEvent(
    resultEvents: EventApi[],
    filters: Task[],
    event: AgendaEvent,
    fitter: Fitter,
    shouldDisplay: boolean,
    startTime: string,
    endTime: string
  ) {
    if (this.filtersAreInterventionVehicleView(filters)) {
      if (event.resources?.length) {
        // Adding generic event for each vehicule column corresponding to the technicians assigned to the event

        fitter.interventionVehicles?.forEach((fitterInterventionVehicle) => {
          const interventionVehicleId =
            this.resManagerService.constructResourceId(
              event.fitterId,
              'INTERVENTION_VEHICLE',
              fitterInterventionVehicle.name
            );

          resultEvents.push(
            this.buildGenericEvent(
              event,
              fitter,
              interventionVehicleId,
              true,
              startTime,
              endTime
            )
          );
        });

        resultEvents.push(
          this.buildGenericEvent(
            event,
            fitter,
            this.resManagerService?.constructDefaultInterventionVehicleId(
              fitter?.id
            ),
            true,
            startTime,
            endTime
          )
        );
      } else {
        // Adding generic event in all the technician columns if no technician is assigned to the event
        fitter.interventionVehicles?.forEach((fitterInterventionVehicle) => {
          const interventionVehicleId =
            this.resManagerService.constructResourceId(
              event.fitterId,
              'INTERVENTION_VEHICLE',
              fitterInterventionVehicle.name
            );

          resultEvents.push(
            this.buildGenericEvent(
              event,
              fitter,
              interventionVehicleId,
              true,
              startTime,
              endTime
            )
          );
        });

        resultEvents.push(
          this.buildGenericEvent(
            event,
            fitter,
            this.resManagerService.constructDefaultInterventionVehicleId(
              event.fitterId
            ),
            true,
            startTime,
            endTime
          )
        );
      }
    } else {
      // Adding Tech leave event for in center intervention column (only if columns view is not technician)
      const interventionVehicleId = this.resManagerService.constructResourceId(
        event.fitterId,
        'OUTSIDE'
      );

      resultEvents.push(
        this.buildGenericEvent(
          event,
          fitter,
          interventionVehicleId,
          shouldDisplay,
          startTime,
          endTime
        )
      );
    }
  }

  getOwnFitterComment(fitter: Fitter) {
    return (
      fitter?.managers?.find(
        (m) => m.email === this.authOktaService.getAccountLogin()
      )?.fitterComment || ' '
    );
  }

  getInterventionTypeLabel(dossier: Dossier, fitter: Fitter) {
    const foundServiceType = findCorrespondingServiceType(
      {
        id: dossier?.appointment?.interventionId,
        glassType: dossier?.claim?.glass?.glass,
        operationType: dossier?.claim?.operationType,
        isMobility: dossier?.appointment?.outsideIntervention,
        replacementVehicle: dossier?.appointment?.replacementVehicle,
        active: true,
        calibration: dossier?.claim?.calibration === 'true',
      },
      fitter?.serviceTypes || []
    );

    return (
      foundServiceType?.name ||
      this.translator.getInstantTranslation(
        'GlobalApplicativeConstants.unknownInterventionLabel'
      )
    );
  }

  getInterventionTypeColor(dossier: Dossier, fitter: Fitter) {
    const foundServiceType = findCorrespondingServiceType(
      {
        id: dossier?.appointment?.interventionId,
        glassType: dossier?.claim?.glass?.glass,
        operationType: dossier?.claim?.operationType,
        isMobility: dossier?.appointment?.outsideIntervention,
        replacementVehicle: dossier?.appointment?.replacementVehicle,
        active: true,
        calibration: dossier?.claim?.calibration === 'true',
      },
      fitter?.serviceTypes || []
    );

    return foundServiceType?.color ?? '#9c9eb3';
  }

  getDossierEventColor(dossier: Dossier, fitter: Fitter) {
    const interventionColor = this.getInterventionTypeColor(dossier, fitter);

    const validationColor = 'var(--color-validation-main)';
    const errorColor = 'var(--color-error-main)';

    if (this.isDossierAbandoned(dossier) || this.isDossierCancelled(dossier)) {
      return errorColor;
    } else {
      return interventionColor;
    }
  }

  getDossierEventBackground(dossier: Dossier, fitter: Fitter) {
    const interventionColor = this.getInterventionTypeColor(dossier, fitter);
    const lightColor = `${interventionColor}50`;
    const lighterColor = `${interventionColor}30`;

    const validationColor = 'var(--color-validation-light)';
    const errorColor = 'var(--color-error-light)';

    if (this.isDossierNoShow(dossier)) {
      return `repeating-linear-gradient(-60deg, ${lightColor}, ${lightColor} 10px, ${lighterColor} 10px, ${lighterColor} 20px)`;
    } else if (
      this.isDossierAbandoned(dossier) ||
      this.isDossierCancelled(dossier)
    ) {
      return `repeating-linear-gradient(-60deg, ${errorColor}, ${errorColor} 10px, rgba(255, 225, 219, 0.2) 10px, rgba(255, 225, 219, 0.2) 20px)`;
    } else if (this.isAppointmentDone(dossier)) {
      return validationColor;
    } else {
      return lighterColor;
    }
  }

  getEventInterventionIconNames(dossier: Dossier): IconName[] {
    const iconNames: IconName[] = [];

    if (
      dossier?.appointment?.outsideIntervention ??
      dossier?.requestAppointment?.outsideIntervention
    ) {
      iconNames.push('home');
    }

    if (
      dossier?.appointment?.isReplacementVehicleSelected ??
      dossier?.requestAppointment?.isReplacementVehicleSelected
    ) {
      if (dossier?.appointment?.assignedReplacementVehicle) {
        iconNames.push('car');
      } else {
        iconNames.push('car_alert');
      }
    }

    if (
      dossier?.appointment?.isConveying ??
      dossier?.requestAppointment?.isConveying
    ) {
      iconNames.push('conveying');
    }

    return iconNames;
  }

  getOriginIcon(origin: DossierGlobalOrigins): IconName {
    return (
      {
        AGENDA: 'fitter',
        PARTNER: 'insurer',
        SITE_WEB: 'device',
        CALL_CENTER: 'phone_call',
        CHALLENGE: 'trophy',
        AICHAT: 'aichat',
      }[origin] ?? 'fitter'
    );
  }

  getPartAvailabilityClass(partAvailability: PartAvailabilityStatus): string {
    return {
      UNAVAILABLE: 'unavailable',
      ORDERED: 'ordered',
      AVAILABLE: 'available',
    }[partAvailability];
  }

  isDossierCancelled(dossier: Dossier): boolean {
    return (
      dossier?.state === 'CANCELLED' ||
      dossier?.appointment?.status === 'CANCELLED' ||
      (!dossier?.appointment?.id &&
        dossier?.requestAppointment?.status === 'CANCELLED')
    );
  }

  isDossierAbandoned(dossier: Dossier): boolean {
    return dossier?.state === 'ABANDONED';
  }

  isDossierNoShow(dossier: Dossier): boolean {
    return (
      dossier?.state === 'NO_SHOW' || dossier?.appointment?.status === 'NO_SHOW'
    );
  }

  isDossierDraft(dossier: Dossier): boolean {
    return (
      !!dossier &&
      (!dossier.id ||
        dossier.state === 'INIT' ||
        dossier.state === 'QUALIFIED' ||
        dossier.state === 'SLOT_SEARCH') &&
      (!dossier.appointment?.status ||
        dossier.appointment?.status === 'DRAFT') &&
      !dossier.requestAppointment
    );
  }

  isAppointmentDone(dossier: Dossier): boolean {
    return dossier?.appointment?.status === 'COMPLETED';
  }

  isDossierReadyToBill(dossier: Dossier): boolean {
    return dossier?.fitterInfo?.isReadyToBill;
  }

  generateIconsForTooltipEvent(
    dossier: Dossier,
    configurationService: ConfigurationService
  ): string {
    const iconTemplateHtml = this.computeIcons(dossier, configurationService);
    return `<div class="dossier-event-icons">
        ${iconTemplateHtml}
      </div>`;
  }

  generateContentForDossier(
    dossier: Dossier,
    fitter: Fitter,
    configurationService: ConfigurationService
  ): string {
    const iconTemplateHtml = this.computeIcons(dossier, configurationService);

    const isShort =
      dayjs(dossier.appointment?.endTime).diff(
        dossier.appointment?.time,
        'minutes'
      ) < 30;

    const shortHideClass = isShort ? 'hide' : '';
    const errorCancelDossierClass = this.isDossierCancelled(dossier)
      ? 'error-cancel'
      : '';

    return `
    <div class="event-container ${errorCancelDossierClass}" style="border-color: ${this.getDossierEventColor(
      dossier,
      fitter
    )}; background: ${this.getDossierEventBackground(dossier, fitter)}">
      <div class="info-text">
        <div class="info model">${dossier.claim.car.model || ''}</div>
        <div class="info immat ${shortHideClass}">${
      dossier.claim.car.licensePlateNumber || ''
    }</div>
        <div class="info lastname ${shortHideClass}">${
      dossier.customer.lastname || ''
    }</div>
      </div>
      <div class="dossier-event-icons">
        ${iconTemplateHtml}
      </div>
    </div>`;
  }

  private computeIcons(
    dossier: Dossier,
    configurationService: ConfigurationService
  ): string {
    const icons: { name: string; classname: string }[] = [];

    if (this.isDossierNoShow(dossier)) {
      icons.push(ICONS.NO_SHOW_APPOINTMENT);
    } else if (this.isAppointmentDone(dossier)) {
      if (this.isDossierReadyToBill(dossier)) {
        icons.push(ICONS.BILL_SENT);
      }
      icons.push(ICONS.APPOINTMENT_COMPLETE);
    } else {
      this.addInterventionIcons(dossier, icons);
      this.addReplacementPartIcon(dossier, icons);
      this.addOriginIcon(dossier, configurationService, icons);
    }

    return this.generateHtmlTemplate(icons);
  }

  private addInterventionIcons(
    dossier: Dossier,
    icons: { name: string; classname: string }[]
  ): void {
    const eventInterventionIconNames =
      this.getEventInterventionIconNames(dossier);
    eventInterventionIconNames.forEach((iconName) => {
      icons.push(Object.values(ICONS).find((icon) => icon.name === iconName));
    });
  }

  private addReplacementPartIcon(
    dossier: Dossier,
    icons: { name: string; classname: string }[]
  ): void {
    const partAvailability = this.getPartAvailabilityClass(
      dossier?.appointment?.partAvailability
    );
    if (dossier.claim.operationType === 'Replace' && partAvailability) {
      icons.push(ICONS[`REPLACEMENT_PART_${partAvailability.toUpperCase()}`]);
    }
  }

  private addOriginIcon(
    dossier: Dossier,
    configurationService: ConfigurationService,
    icons: { name: string; classname: string }[]
  ): void {
    const originIcon = this.getOriginIcon(
      configurationService.getDossierGlobalOriginFromCreationData(
        dossier?.creationData,
        this.clientAppId
      )
    );
    icons.push(Object.values(ICONS).find((icon) => icon.name === originIcon));
  }

  private generateHtmlTemplate(
    icons: { name: string; classname: string }[]
  ): string {
    return icons
      .filter((icon) => !!icon)
      .map(
        (icon) =>
          `<span class="planning-icon ${icon.classname}" style="background-image: url('/assets/default/icons.new/${icon.name}.svg');"></span>`
      )
      .join(' ');
  }

  generateContentForCustomEvent(event: AgendaEvent) {
    const start = dayjs(event.startTime).tz(
      event.timezone || this.timezoneService?.getDefaultTimezone()
    );
    const end = dayjs(event.endTime).tz(
      event.timezone || this.timezoneService?.getDefaultTimezone()
    );

    const startDay = start.format('L');
    const endDay = end.format('L');
    const startTime = start.format('LT');
    const endTime = end.format('LT');

    const diff = end.diff(start, 'minutes');

    const isCloseEvent =
      event.type === 'SPECIAL_CLOSING' || event.type === 'HOLYDAY';

    const isUnavailabilityEvent =
      event.type === 'TECH_LEAVE' ||
      event.type === 'INTERVENTION_VEHICLE_LEAVE' ||
      event.type === 'RV_LEAVE';

    const currentFilters = this.storage.getItem<JsonListKey, Task>(
      'agenda-resource-filters'
    );

    const isTechAndCondensed =
      event.type === 'TECH_LEAVE' &&
      !this.filtersAreTechnicianView(currentFilters);
    const isIVAndCondensed =
      event.type === 'INTERVENTION_VEHICLE_LEAVE' &&
      !this.filtersAreInterventionVehicleView(currentFilters);
    const isRVAndCondensed =
      event.type === 'RV_LEAVE' && !this.filtersAreRVView(currentFilters);

    return `
    <div class="event-container ${isCloseEvent ? 'closing-event' : ''} ${
      isUnavailabilityEvent ? 'unavailability-event' : ''
    }">
      <div class="info-text">
        ${
          isCloseEvent || isUnavailabilityEvent
            ? `<div class="info title event-type">${this.translator.getInstantTranslation(
                `PlanningAgenda.eventTypes.${event.type}`
              )}</div>`
            : ''
        }
        <div class="info title">${this.translator.getInstantTranslationOrValue(
          event.title
        )}</div>
        ${
          (isTechAndCondensed || isIVAndCondensed || isRVAndCondensed) &&
          event.resources
            ? `<div class="info resources">${event.resources
                .map((res) => res.name ?? '')
                .join(' - ')}</div>`
            : ''
        }
        ${
          diff < 50
            ? ''
            : startDay !== endDay
            ? `
            <div class="info">
            ${this.translator.getInstantTranslation(
              'PlanningEventTooltip.dateMultipleDays',
              { startDay, startTime, endDay, endTime }
            )}
            </div>
            <div class="info">${
              event.comment?.replace('\n', '<br/>') || ''
            }</div>
            `
            : `
          <div class="info">${this.translator.getInstantTranslation(
            'PlanningEventTooltip.dateSingleDay',
            { startDay, startTime, endTime }
          )}
          </div>
          <div class="info">${event.comment?.replace('\n', '<br/>') || ''}</div>
          `
        }

      </div>
    </div>
    `;
  }

  waitForNextCalendarResize(
    calendarElToWatch = '',
    intervalMs = 100,
    maxIterations = 10
  ): Observable<void> {
    let fullCalendarEl = document.querySelector(
      `full-calendar ${calendarElToWatch}`
    );
    const originalWidth = fullCalendarEl?.clientWidth;

    return interval(intervalMs).pipe(
      map(
        (currentIteration) =>
          currentIteration >= maxIterations ||
          fullCalendarEl?.clientWidth !== originalWidth
      ),
      tap((isChanged) => {
        if (!isChanged && !fullCalendarEl) {
          fullCalendarEl = document.querySelector(
            `full-calendar ${calendarElToWatch}`
          );
        }
      }),
      filter((isChanged) => isChanged),
      map(noop),
      take(1)
    );
  }

  isEventRecurrent(event: AgendaEvent): event is RecurrentAgendaEvent {
    return !!(event as RecurrentAgendaEvent)?.recurrent;
  }

  isEventRecurrenceInstance(
    event: AgendaEvent
  ): event is RecurrentInstanceAgendaEvent {
    return !!(event as RecurrentInstanceAgendaEvent)?.generatedFrom;
  }

  //#region

  /**
   * FullCalendar cannot handle multiple timezone so if entry is
   * time = 07:00 and timezone = Europe/paris then it means 09:00 in Paris
   * To display time properly we have to apply this offset manually in order to show the event at 09:00 in the calendar
   * and remove it as soon as the event is not in the calendar anymore
   * Events are shown at the local time of the center
   */

  /**
   * Add offset to time
   *
   * @param time Time without offset
   * @param timezone Timezone of the center
   * @returns Time with offset added
   */
  computeTimeOffsetForCalendar = (
    time: string | Date,
    timezone: string
  ): string => {
    if (time && timezone) {
      const offset = dayjs(time).tz(timezone)?.utcOffset();
      return dayjs(time).add(offset, 'm').toISOString();
    }
  };

  /**
   * Subtract offset to the time
   *
   * @param time Time with offset
   * @param timezone Timezone of the center
   * @returns Time with offset removed
   */
  computeTimeOffsetOffCalendar = (
    time: string | Date,
    timezone: string
  ): string => {
    const offset = dayjs(time).tz(timezone)?.utcOffset();
    return dayjs(time).subtract(offset, 'm').toISOString();
  };

  //#endregion
}
